import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isWithinInterval } from 'date-fns';
import { isEpisode } from '@24i/nxg-sdk-photon/src/models/episode';
import { BLOCKING_REASON_TYPES, ASSET_TYPE } from '@24i/nxg-sdk-photon';
import { modalType, usePlayerUIContext, setIsOnLiveEdge, } from '@24i/player-ui';
import { useThrottleFn, usePrevious, useUpdateEffect } from '@24i/nxg-core-utils';
import { useEventListener } from '@24i/player-utils-react';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { SOTT_DEFAULT_WEB_SCREENS } from '@24i/nxg-sdk-smartott/src/navigation/constants';
import ActionButton from '@24i/nxg-sdk-gluons/src/components/buttons/ActionButton';
import { PermanentContainer } from '../../PermanentContainer';
import { Feedback } from '../../Feedback';
import { HideableContainer } from '../../HideableContainer';
import { SettingsModal, EpisodesModal, EpgModal, BlockingMessageContainer, unlockButtonStyles, BlockingMessage, RelatedModal, Background, PermanentFooter, AdCountdownContainer, BingeWatchingContainer, } from './styled';
import { PlayerControls, PlayerFooter, PlayerHeader, BingeWatchingOverlay, BlockingMessageIcon, } from './components';
import { Center, Container, Footer } from '../components';
import { AdCountdown } from '../../AdCountdown';
import { SkipAdButton } from '../../SkipAdButton';
export const RegularUI = ({ playerInstance, useDurationOverRemaining, shouldShowBufferedRanges = true, shouldShowProgressTime = false, shouldShowFooterPlayerButtons = true, shouldShowAirtime = false, styles, onBack, asset, seriesData, epgData, relatedItems, timeline, blockingReason, blockingMessage, dvrWindowOverride, onUnlockAction, onPlayPausePress, onCurrentProgramChange, onGoToNextEpisode, headerOffset, }) => {
    var _a;
    const { t } = useTranslation(['sott']);
    const router = useRouter();
    const viewportHeight = '100dvh';
    const { dispatch, state: { isSeeking, isDragSeeking, isAnyModalOpen, bingeWatchingOverlay }, } = usePlayerUIContext();
    const isBingeWatchingEnabled = (_a = useFeature('binge_watching')) === null || _a === void 0 ? void 0 : _a.enabled;
    const getCurrentChannel = useCallback(() => {
        const currentChannelByFlag = epgData === null || epgData === void 0 ? void 0 : epgData.channels.find((channel) => channel.isCurrentlyPlaying);
        return (currentChannelByFlag ||
            (epgData === null || epgData === void 0 ? void 0 : epgData.channels.find((channel) => { var _a, _b; return ((_a = channel.channel) === null || _a === void 0 ? void 0 : _a.id) === ((_b = asset.channel) === null || _b === void 0 ? void 0 : _b.id); })));
    }, [epgData]);
    const getCurrentProgram = (channel) => {
        var _a, _b;
        if (!(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.live)) {
            return null;
        }
        const time = (playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.getUTCTimestamp(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.currentTime)) || new Date();
        const channelCurrentProgram = (_b = (_a = channel === null || channel === void 0 ? void 0 : channel.channel) === null || _a === void 0 ? void 0 : _a.programs) === null || _b === void 0 ? void 0 : _b.find((program) => {
            if (!program.start || !program.end)
                return false;
            return isWithinInterval(time, { start: program.start, end: program.end });
        });
        return channelCurrentProgram || null;
    };
    const [currentChannel, setCurrentChannel] = useState(getCurrentChannel);
    const [currentProgram, setCurrentProgram] = useState(() => getCurrentProgram(currentChannel));
    const [isDisplayed, setIsDisplayed] = useState(false);
    const isUnlockable = [BLOCKING_REASON_TYPES.AGE, BLOCKING_REASON_TYPES.ADULT].includes(blockingReason);
    const prevBlockingReason = usePrevious(blockingReason);
    useEffect(() => {
        if (blockingReason && (playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.playing))
            playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.pause();
        if (prevBlockingReason && !blockingReason && (playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.paused))
            playerInstance.play();
    }, [blockingReason, playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.playing]);
    const onPause = useCallback(() => {
        dispatch(setIsOnLiveEdge(null));
    }, [playerInstance]);
    const updateIsOnLiveEdge = useCallback(() => {
        dispatch(setIsOnLiveEdge(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.isOnLiveEdge()));
    }, [playerInstance]);
    useUpdateEffect(() => {
        const nextCurrentChannel = getCurrentChannel();
        setCurrentChannel(nextCurrentChannel);
        setCurrentProgram(getCurrentProgram(nextCurrentChannel));
    }, [epgData]);
    useEffect(() => onCurrentProgramChange === null || onCurrentProgramChange === void 0 ? void 0 : onCurrentProgramChange(currentProgram), [currentProgram]);
    const { run: onTimeUpdate } = useThrottleFn(() => {
        if (!playerInstance.live) {
            return;
        }
        const time = playerInstance.getUTCTimestamp(playerInstance.currentTime) || new Date();
        if (!currentProgram || !isWithinInterval(time, currentProgram)) {
            setCurrentProgram(getCurrentProgram(currentChannel));
        }
    }, {
        wait: 1000,
    });
    useEventListener('pause', onPause, playerInstance);
    useEventListener('seeked', updateIsOnLiveEdge, playerInstance);
    useEventListener('playing', updateIsOnLiveEdge, playerInstance);
    useEventListener('timeupdate', onTimeUpdate, playerInstance);
    const hasNextEpisode = Boolean((seriesData === null || seriesData === void 0 ? void 0 : seriesData.nextEpisode) || (isEpisode(asset) && asset.nextEpisodeId));
    const showButton = isUnlockable || blockingReason === BLOCKING_REASON_TYPES.SUBSCRIPTION_TYPE;
    const goToDetails = () => {
        const id = asset.type === ASSET_TYPE.EPISODE ? asset.seriesId || asset.series : asset.id;
        const type = asset.type === ASSET_TYPE.EPISODE ? ASSET_TYPE.SERIES : asset.type;
        const detailLink = SOTT_DEFAULT_WEB_SCREENS.Details.getLink({
            type,
            id,
        });
        router === null || router === void 0 ? void 0 : router.push(detailLink.href, detailLink.as);
    };
    let isContainerDisplayed;
    if (bingeWatchingOverlay.isOpen) {
        isContainerDisplayed = false;
    }
    else if (isDragSeeking || isAnyModalOpen || !!blockingReason) {
        isContainerDisplayed = true;
    }
    const backgroundStyle = useMemo(() => ({
        maxHeight: viewportHeight,
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        position: 'relative',
        boxSizing: 'border-box',
    }), [viewportHeight]);
    return (React.createElement(Background, { blockingReason: blockingReason, style: backgroundStyle },
        React.createElement(HideableContainer, { displayed: isContainerDisplayed, onDisplayedChange: setIsDisplayed },
            React.createElement(Container, null,
                React.createElement(PlayerHeader, { onBack,
                    asset,
                    seriesData,
                    currentProgram,
                    blockingReason,
                    shouldShowAirtime, key: currentProgram === null || currentProgram === void 0 ? void 0 : currentProgram.id, isSeeking: isSeeking, headerOffset: headerOffset }),
                React.createElement(PlayerControls, { asset,
                    playerInstance,
                    epgData,
                    blockingReason,
                    onUnlockAction,
                    onPlayPausePress }),
                React.createElement(PlayerFooter, { assetType: asset.type, isStartOver: asset.isStartOver, hasMoreEpisodes: Boolean(seriesData &&
                        seriesData.series &&
                        seriesData.seasons &&
                        seriesData.seasons.length), hasRelated: Boolean(relatedItems && relatedItems.length), showHours: "auto", playerInstance,
                    asset,
                    seriesData,
                    useDurationOverRemaining,
                    shouldShowBufferedRanges,
                    shouldShowProgressTime,
                    shouldShowFooterPlayerButtons,
                    isSeeking,
                    epgData,
                    blockingReason,
                    currentChannel,
                    currentProgram,
                    dvrWindowOverride,
                    timeline,
                    styles })),
            React.createElement(SettingsModal, { type: modalType.settings, playerInstance }),
            React.createElement(EpisodesModal, { type: modalType.episodes, shouldBePausedWhenOpen: true, playerInstance, seriesData }),
            React.createElement(EpgModal, { type: modalType.channels, shouldBePausedWhenOpen: true, playerInstance, epgData }),
            React.createElement(RelatedModal, { type: modalType.related, shouldBePausedWhenOpen: true, playerInstance, relatedItems })),
        React.createElement(PermanentContainer, null,
            React.createElement(Center, null,
                !blockingReason && React.createElement(Feedback, { playerInstance }),
                blockingReason && (React.createElement(BlockingMessageContainer, null,
                    showButton ? (React.createElement(ActionButton, { title: t(`player.${isUnlockable ? 'unlock' : 'purchaseNeededButton'}`), additionalContainerStyles: unlockButtonStyles, onPress: isUnlockable ? onUnlockAction : goToDetails })) : (React.createElement(BlockingMessageIcon, { blockingReason, onUnlockAction })),
                    React.createElement(BlockingMessage, { blockingReason, blockingMessage })))),
            React.createElement(Footer, null),
            React.createElement(PermanentFooter, { hidden: isAnyModalOpen },
                isBingeWatchingEnabled && (React.createElement(BingeWatchingContainer, { hidden: !isDisplayed && bingeWatchingOverlay.isDismissed },
                    React.createElement(BingeWatchingOverlay, { playerInstance,
                        asset,
                        timeline,
                        hasNextEpisode,
                        onGoToNextEpisode }))),
                React.createElement(AdCountdownContainer, null,
                    React.createElement(AdCountdown, { playerInstance: playerInstance }),
                    React.createElement(SkipAdButton, { playerInstance: playerInstance }))))));
};
RegularUI.displayName = 'RegularUI';
//# sourceMappingURL=index.js.map